import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const SLICES = [
  ["pnc", "PNCs", "#DC2626"],
  ["investigating", "Investigating", "#3B82F6"],
  ["near_miss", "Near Miss", "#F59E0B"],
  ["resolved_no_finding", "Resolved – No Finding", "#10B981"],
  ["other_resolved", "Other resolved", "#94A3B8"],
];

function Donut({ split, total, size = 132 }) {
  const stroke = 20, r = (size - stroke) / 2, c = size / 2, circ = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0" data-testid="incident-donut">
      <circle cx={c} cy={c} r={r} fill="none" stroke="#E2E8F0" strokeWidth={stroke} />
      {total > 0 && SLICES.map(([k, , color]) => {
        const len = circ * ((split[k] || 0) / total);
        const el = len > 0 && (
          <circle key={k} cx={c} cy={c} r={r} fill="none" stroke={color} strokeWidth={stroke}
            strokeDasharray={`${len} ${circ - len}`} strokeDashoffset={-offset} transform={`rotate(-90 ${c} ${c})`} />
        );
        offset += len;
        return el;
      })}
      <text x={c} y={c - 2} textAnchor="middle" className="fill-current font-bold" style={{ fontSize: 26 }}>{total}</text>
      <text x={c} y={c + 16} textAnchor="middle" className="fill-current opacity-60 uppercase" style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1 }}>reported</text>
    </svg>
  );
}

const Stat = ({ label, value, testid, tone = "" }) => (
  <div>
    <div className={`text-3xl font-display font-black tabular-nums ${tone}`} data-testid={testid}>{value}</div>
    <div className="overline text-muted-foreground mt-1">{label}</div>
  </div>
);

export function IncidentProfileChart({ refreshKey }) {
  const [period, setPeriod] = useState("all");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/incidents/stats/radar", { params: { period } })
      .then(({ data: d }) => setData(d))
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); setPeriod("all"); });
  }, [period, refreshKey]);

  const counts = data?.counts || {};
  const split = counts.split || {};
  const total = counts.reported || 0;
  const ap = data?.audit_period;
  const scope = period === "audit" && ap ? `audit period ${ap.start || "…"} → ${ap.end || "…"}`
    : period.startsWith("year:") ? `discovered in ${period.slice(5)}` : "all time";

  return (
    <section className="border border-border bg-card p-5" data-testid="incident-profile">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="overline text-muted-foreground">
          Incident profile <span className="text-[10px] normal-case font-normal text-muted-foreground/70">({scope})</span>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="rounded-none w-56 h-9" data-testid="incident-radar-period"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All time</SelectItem>
            <SelectItem value="audit" disabled={!ap}>Last audit period{ap ? "" : " (none set)"}</SelectItem>
            {(data?.years || []).map((y) => <SelectItem key={y} value={`year:${y}`}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-8 flex-wrap">
        <Stat label="Reported" value={total} testid="incident-radar-count-reported" />
        <Stat label="PNCs" value={counts.pnc ?? 0} testid="incident-radar-count-pnc" tone="text-red-600" />
        <Stat label="Investigating" value={counts.investigating ?? 0} testid="incident-radar-count-investigating" tone="text-blue-600" />
        <Stat label="Near Miss" value={counts.near_miss ?? 0} testid="incident-radar-count-near_miss" tone="text-amber-600" />
        <Stat label="Resolved – No Finding" value={counts.resolved_no_finding ?? 0} testid="incident-radar-count-resolved_no_finding" tone="text-emerald-600" />
        <Stat label="Overdue" value={counts.overdue ?? 0} testid="incident-count-overdue" tone={counts.overdue ? "text-red-600" : ""} />
      </div>
      <div className="mt-4 pt-4 border-t border-border">
        <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-2">Disposition split</div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <Donut split={split} total={total} />
          <div className="text-xs space-y-1.5">
            {SLICES.filter(([k]) => k !== "other_resolved" || split[k]).map(([k, label, color]) => (
              <div key={k} className="flex items-center gap-2" data-testid={`incident-split-${k}`}>
                <span className="inline-block w-3 h-3 rounded-sm" style={{ background: color }} />
                <span className="font-semibold">{label}</span>
                <span className="tabular-nums font-mono">{split[k] || 0}</span>
                {total > 0 && <span className="text-muted-foreground">({Math.round(((split[k] || 0) / total) * 100)}%)</span>}
              </div>
            ))}
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-3">Each incident counted once: PNC → Near Miss → Resolved – No Finding → Investigating (open).</p>
      </div>
    </section>
  );
}
